import type { BrowserCommentTarget } from "@cmmd-forge/contracts";

/**
 * Label for the browser element a comment is anchored to, shown as a chip in
 * the composer.
 *
 * Ported in spirit from t3code's `formatElementContextLabel`, adapted to
 * Forge's producer. t3code carries a CSS selector and an HTML preview it can
 * name the element by; Forge's `BrowserCommentTarget` carries geometry — a
 * point and/or a rect on a captured page — so the label describes *where* on
 * the page rather than *what* element, because that is the fact Forge actually
 * has. Inventing a selector-shaped label from a rect would read as precision
 * that is not there.
 */
export interface ComposerElementContextLabel {
  readonly label: string;
  readonly detail: string | null;
}

function hostOf(url: string): string | null {
  try {
    return new URL(url).host || null;
  } catch {
    return null;
  }
}

export function formatComposerElementContextLabel(
  target: BrowserCommentTarget,
): ComposerElementContextLabel {
  const host = hostOf(target.url);

  // A rect is a selection; a bare point is a click. Saying "Region" for a
  // single-pixel anchor would overstate what the user marked.
  const shape = target.rect
    ? `Region ${Math.round(target.rect.width)}×${Math.round(target.rect.height)}`
    : target.point
      ? `Point ${Math.round(target.point.x)}, ${Math.round(target.point.y)}`
      : "Page";

  return {
    label: host ? `${shape} on ${host}` : shape,
    detail: target.url,
  };
}
